import { z } from "zod";

import type { CaptionIssue } from "../lib/academy-captions";
import { ACADEMY_VIDEO_INPUT } from "../lib/academy-video";
import type { VideoViolation, VideoWarning } from "../lib/academy-video";
import { instructorLinksSchema } from "./academy-instructor-links";

/** The fixed set of Academy Course categories (stored as text on the Course). */
export const ACADEMY_CATEGORIES = [
  "academics",
  "exam-prep",
  "language",
  "technology",
  "business",
  "design",
  "personal-development",
  "other",
] as const;

export const academyCategorySchema = z.enum(ACADEMY_CATEGORIES);

/** How demanding a Course is. `all` means no particular level. */
export const ACADEMY_LEVELS = [
  "all",
  "beginner",
  "intermediate",
  "advanced",
] as const;

export const academyLevelSchema = z.enum(ACADEMY_LEVELS);

/**
 *
 */
export type AcademyLevel = z.infer<typeof academyLevelSchema>;

/** The languages a Course can be taught in (Bengali first). */
export const ACADEMY_LANGUAGES = ["bn", "en"] as const;

export const academyLanguageSchema = z.enum(ACADEMY_LANGUAGES);

/**
 *
 */
export type AcademyLanguage = z.infer<typeof academyLanguageSchema>;

/**
 *
 */
export type AcademyCategory = z.infer<typeof academyCategorySchema>;

/** The public name of a Course's instructor. */
export const catalogInstructorSchema = z.object({
  /** The instructor's Publisher id: lets a page list their other Courses. */
  id: z.string(),
  name: z.string(),
  headline: z.string().nullable(),
  /** The instructor's account photo, or null when they have not set one. */
  photoUrl: z.string().nullable(),
});

/** One Course as the public Academy Catalog shows it (`GET /v1/courses`). */
export const catalogCourseSchema = z.object({
  id: z.string(),
  slug: z.string(),
  title: z.string(),
  cover: z.string().nullable(),
  /** `/v1/courses/{slug}/cover` when the Release has a cover, else null. */
  coverUrl: z.string().nullable(),
  /** One line under the title; empty on Courses published before subtitles. */
  subtitle: z.string(),
  description: z.string(),
  category: academyCategorySchema,
  level: academyLevelSchema,
  instructor: catalogInstructorSchema,
  lessonCount: z.number().int().nonnegative(),
  totalDurationSeconds: z.number().int().nonnegative(),
  outcomes: z.array(z.string()),
  /** BDT amount as a decimal string, e.g. "499.00". Never a float. */
  price: z.string(),
  currency: z.string(),
  /** Active Learners across free and paid access; lets the Catalog sort by popularity. */
  studentCount: z.number().int().nonnegative().default(0),
  /** When the current Release was published (ISO 8601); lets the Catalog sort by newest. */
  publishedAt: z.string().default(""),
  /** Mean of the Learners' ratings to one decimal, or null before anyone rated. */
  ratingAverage: z.number().min(1).max(5).nullable().default(null),
  /** How many Learners rated the Course. */
  ratingCount: z.number().int().nonnegative().default(0),
});

export const catalogResponseSchema = z.object({
  courses: z.array(catalogCourseSchema),
});

/** Query string of `GET /v1/courses`: free-text search and a category filter. */
export const catalogQuerySchema = z.object({
  q: z.string().trim().max(100).optional(),
  category: academyCategorySchema.optional(),
});

/**
 *
 */
export type CatalogQuery = z.infer<typeof catalogQuerySchema>;

/**
 *
 */
export type CatalogCourse = z.infer<typeof catalogCourseSchema>;

/**
 *
 */
export type CatalogResponse = z.infer<typeof catalogResponseSchema>;

const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/** Decimal BDT string with at most two places, 0 or more. */
const PRICE_RE = /^\d+(?:\.\d{1,2})?$/;

export const courseSlugSchema = z
  .string()
  .min(1)
  .max(80)
  .regex(SLUG_RE, "Use lowercase letters, digits and single hyphens");

/**
 *
 */
export const coursePriceSchema = z
  .union([z.string(), z.number()])
  .transform((value) => String(value))
  .refine((value) => PRICE_RE.test(value), {
    message: "Price must be 0 or more, with at most two decimal places",
  });

// Create fills in defaults; update must not: `.partial()` keeps a field's
// `.default()`, so a PATCH of just the title would reset the description,
// price, outcomes and category. Updates use the plain fields.
/** Longest subtitle an instructor can write. */
export const SUBTITLE_MAX = 160;

const courseBaseSchema = z.object({
  title: z.string().trim().min(1).max(200),
  slug: courseSlugSchema,
  cover: z.string().max(500).nullable().optional(),
  description: z.string().max(5000),
  category: academyCategorySchema,
  outcomes: z.array(z.string().trim().min(1).max(300)).max(20),
  subtitle: z.string().trim().max(SUBTITLE_MAX),
  level: academyLevelSchema,
  language: academyLanguageSchema,
  requirements: z.array(z.string().trim().min(1).max(300)).max(10),
  audience: z.array(z.string().trim().min(1).max(300)).max(10),
  price: coursePriceSchema,
});

const courseFieldsSchema = courseBaseSchema.extend({
  description: courseBaseSchema.shape.description.default(""),
  category: courseBaseSchema.shape.category.default("other"),
  outcomes: courseBaseSchema.shape.outcomes.default([]),
  subtitle: courseBaseSchema.shape.subtitle.default(""),
  level: courseBaseSchema.shape.level.default("all"),
  language: courseBaseSchema.shape.language.default("bn"),
  requirements: courseBaseSchema.shape.requirements.default([]),
  audience: courseBaseSchema.shape.audience.default([]),
  price: courseBaseSchema.shape.price.default("0"),
});

/**
 * Creating a Course needs only a title; the slug is made from it when left
 * out (instructors should not have to invent a web address to start).
 */
export const createCourseSchema = courseFieldsSchema.extend({
  slug: courseSlugSchema.optional(),
});
export const updateCourseSchema = courseBaseSchema.partial();

export const chapterTitleSchema = z.object({
  title: z.string().trim().min(1).max(200),
});

export const createChapterSchema = chapterTitleSchema;
export const updateChapterSchema = chapterTitleSchema;

// Same split as the course schemas: defaults on create only, never on update.
const lessonBaseSchema = z.object({
  title: z.string().trim().min(1).max(200),
  learningObjective: z.string().trim().min(1).max(300),
  keyPoints: z.array(z.string().trim().min(1).max(300)).max(10),
  freePreview: z.boolean(),
});

const lessonFieldsSchema = lessonBaseSchema.extend({
  keyPoints: lessonBaseSchema.shape.keyPoints.default([]),
  freePreview: lessonBaseSchema.shape.freePreview.default(false),
});

export const createLessonItemSchema = lessonFieldsSchema;
export const updateLessonItemSchema = lessonBaseSchema.partial();

/** What a Knowledge Check asks (ADR 0010: the Release freezes the questions). */
export const questionKindSchema = z.enum([
  "single_choice",
  "multiple_choice",
  "true_false",
]);

/**
 *
 */
export type QuestionKind = z.infer<typeof questionKindSchema>;

/** A Knowledge Check holds one to five questions (glossary). */
export const KNOWLEDGE_CHECK_MIN_QUESTIONS = 1;
export const KNOWLEDGE_CHECK_MAX_QUESTIONS = 5;
export const KNOWLEDGE_CHECK_MIN_OPTIONS = 2;
export const KNOWLEDGE_CHECK_MAX_OPTIONS = 6;

/** The Pass Mark a new Knowledge Check starts with, in percent. */
export const DEFAULT_PASS_MARK = 70;

/** Ids of a true or false question's two options, in this order. */
export const TRUE_FALSE_OPTION_IDS = ["true", "false"] as const;

/**
 * The role a Knowledge Check can play in measuring learning gain: the Pre-test
 * a Learner is offered when starting the Course, or its matched Post-test,
 * offered from the completion screen. A Course has at most one of each. Both
 * are left out of Course Completion and of progress percentages.
 */
export const testRoleSchema = z.enum(["pre", "post"]);

/**
 *
 */
export type TestRole = z.infer<typeof testRoleSchema>;

const CONTENT_ID_RE = /^[A-Za-z0-9_-]{1,64}$/;

/**
 * A question option as an Author writes it. The draft may be incomplete (empty
 * text, a missing explanation): publish refuses it, saving does not. An option
 * without an `id` gets one from the server and keeps it from then on.
 */
export const knowledgeCheckOptionInputSchema = z.object({
  id: z.string().regex(CONTENT_ID_RE).optional(),
  text: z.string().trim().max(300).default(""),
  correct: z.boolean().default(false),
  explanation: z.string().trim().max(1000).default(""),
});

export const knowledgeCheckQuestionInputSchema = z.object({
  id: z.string().regex(CONTENT_ID_RE).optional(),
  kind: questionKindSchema,
  prompt: z.string().trim().max(500).default(""),
  options: z
    .array(knowledgeCheckOptionInputSchema)
    .min(KNOWLEDGE_CHECK_MIN_OPTIONS)
    .max(KNOWLEDGE_CHECK_MAX_OPTIONS),
});

const knowledgeCheckTitleFields = {
  title: z.string().trim().min(1).max(200),
  learningObjective: z.string().trim().min(1).max(300),
};

const passMarkSchema = z.number().int().min(1).max(100);

const questionsSchema = z
  .array(knowledgeCheckQuestionInputSchema)
  .max(KNOWLEDGE_CHECK_MAX_QUESTIONS);

export const createKnowledgeCheckSchema = z.object({
  ...knowledgeCheckTitleFields,
  passMark: passMarkSchema.default(DEFAULT_PASS_MARK),
  questions: questionsSchema.default([]),
  /** Flags the check as the Course's Pre-test or Post-test; absent or null for neither. */
  testRole: testRoleSchema.nullish(),
});

/** No defaults here: an omitted field must leave the stored value alone. */
export const updateKnowledgeCheckSchema = z.object({
  title: knowledgeCheckTitleFields.title.optional(),
  learningObjective: knowledgeCheckTitleFields.learningObjective.optional(),
  passMark: passMarkSchema.optional(),
  questions: questionsSchema.optional(),
  /** A role flags the check; null clears the flag; omitted leaves it alone. */
  testRole: testRoleSchema.nullable().optional(),
});

/**
 *
 */
export type KnowledgeCheckQuestionInput = z.infer<
  typeof knowledgeCheckQuestionInputSchema
>;

/**
 *
 */
export type CreateKnowledgeCheckInput = z.infer<
  typeof createKnowledgeCheckSchema
>;

/**
 *
 */
export type UpdateKnowledgeCheckInput = z.infer<
  typeof updateKnowledgeCheckSchema
>;

/** One answer option with its key and its explanation. Authors and Releases only. */
export interface KnowledgeCheckOption {
  correct: boolean;
  explanation: string;
  id: string;
  text: string;
}

/**
 *
 */
export interface KnowledgeCheckQuestion {
  id: string;
  kind: QuestionKind;
  options: KnowledgeCheckOption[];
  prompt: string;
}

/** A Knowledge Check's questions and Pass Mark, answer keys included. */
export interface KnowledgeCheckContent {
  /** Percent of questions to answer correctly, 1 to 100. */
  passMark: number;
  questions: KnowledgeCheckQuestion[];
  /** The Pre-test or Post-test flag; absent or null for an ordinary check. */
  testRole?: TestRole | null;
}

/** Limits of a Written Response (ticket 15). Lengths count characters. */
export const WRITTEN_RESPONSE_PROMPT_MAX = 2000;
export const WRITTEN_RESPONSE_MODEL_ANSWER_MAX = 5000;

/** The longest minimum an Author may ask for (a Learner can always meet it). */
export const WRITTEN_RESPONSE_MIN_LENGTH_MAX = 5000;

/** The longest answer a Learner may submit. */
export const WRITTEN_RESPONSE_BODY_MAX = 10_000;

/** The minimum length a new Written Response starts with. */
export const DEFAULT_MIN_LENGTH = 20;

const writtenResponseTitleFields = {
  title: z.string().trim().min(1).max(200),
  learningObjective: z.string().trim().min(1).max(300),
};

const promptSchema = z.string().trim().max(WRITTEN_RESPONSE_PROMPT_MAX);

const modelAnswerSchema = z
  .string()
  .trim()
  .max(WRITTEN_RESPONSE_MODEL_ANSWER_MAX);

const minLengthSchema = z
  .number()
  .int()
  .min(0)
  .max(WRITTEN_RESPONSE_MIN_LENGTH_MAX);

/**
 * A Written Response as an Author writes it. Like a Knowledge Check the draft
 * may be unfinished (empty prompt or Model Answer, a minimum length of 0):
 * publish refuses it, saving does not.
 */
export const createWrittenResponseSchema = z.object({
  ...writtenResponseTitleFields,
  prompt: promptSchema.default(""),
  modelAnswer: modelAnswerSchema.default(""),
  minLength: minLengthSchema.default(DEFAULT_MIN_LENGTH),
});

/** No defaults here: an omitted field must leave the stored value alone. */
export const updateWrittenResponseSchema = z.object({
  title: writtenResponseTitleFields.title.optional(),
  learningObjective: writtenResponseTitleFields.learningObjective.optional(),
  prompt: promptSchema.optional(),
  modelAnswer: modelAnswerSchema.optional(),
  minLength: minLengthSchema.optional(),
});

/**
 *
 */
export type CreateWrittenResponseInput = z.infer<
  typeof createWrittenResponseSchema
>;

/**
 *
 */
export type UpdateWrittenResponseInput = z.infer<
  typeof updateWrittenResponseSchema
>;

/** A Written Response's prompt, Model Answer and minimum length. Authors and Releases only. */
export interface WrittenResponseContent {
  /** The Author's reference answer, shown only after a submission. */
  modelAnswer: string;
  /** Characters the Learner must write (after trimming) to complete the Item. */
  minLength: number;
  prompt: string;
}

/** Full ordering of the children of one parent, as ids in the new order. */
export const reorderSchema = z.object({
  ids: z.array(z.string().min(1)).max(500),
});

/** Body of `POST /v1/academy/items/:itemId/move`: where an Item goes, in the same Course. */
export const moveItemSchema = z.object({
  chapterId: z.string().min(1),
  /** Zero-based place among the target Chapter's other Items; past the end means last. */
  position: z.number().int().min(0).max(500),
});

/**
 *
 */
export type MoveItemInput = z.infer<typeof moveItemSchema>;

/**
 *
 */
export type CreateCourseInput = z.infer<typeof createCourseSchema>;

/**
 *
 */
export type UpdateCourseInput = z.infer<typeof updateCourseSchema>;

/**
 *
 */
export type CreateLessonItemInput = z.infer<typeof createLessonItemSchema>;

/**
 *
 */
export type UpdateLessonItemInput = z.infer<typeof updateLessonItemSchema>;

/**
 *
 */
export type ReorderInput = z.infer<typeof reorderSchema>;

/** Where a Lesson's newest, unfinished video upload stands (browser upload and conversion). */
export type AuthoringVideoUploadStatus =
  | "pending_upload"
  | "processing"
  | "failed";

/**
 * A video the Author has sent (or is sending) that is not playable yet. The
 * Lesson keeps its previous video until the new one is `ready`.
 */
export interface AuthoringVideoUpload {
  assetId: string;
  /** Plain-words reason when `status` is `failed`. */
  failureReason: string | null;
  sizeBytes: number | null;
  status: AuthoringVideoUploadStatus;
}

/**
 * One presigned part of a browser upload: PUT bytes `start` up to (not
 * including) `end` of the file to `url`.
 */
export interface VideoUploadPart {
  end: number;
  partNumber: number;
  start: number;
  url: string;
}

/** The plan the API gives the browser for a direct multipart upload. */
export interface VideoUploadPlan {
  assetId: string;
  parts: VideoUploadPart[];
}

/** Body of `POST /v1/academy/items/:itemId/video/uploads`. */
export const createVideoUploadSchema = z.object({
  fileName: z.string().trim().min(1).max(255),
  contentType: z.enum(ACADEMY_VIDEO_INPUT.contentTypes),
  sizeBytes: z.number().int().positive().max(ACADEMY_VIDEO_INPUT.maxBytes),
});

/**
 *
 */
export type CreateVideoUploadInput = z.infer<typeof createVideoUploadSchema>;

/** A Lesson's current video as the authoring editor shows it. */
export interface AuthoringVideo {
  assetId: string;
  audioCodec: string | null;
  bitrateBps: number;
  durationSeconds: number;
  height: number;
  /** Short-lived link to the chosen poster frame, or null before one is picked. */
  posterUrl: string | null;
  /** Short-lived link to the video itself, for the Author's preview. */
  previewUrl: string;
  sizeBytes: number;
  videoCodec: string;
  warnings: VideoWarning[];
  width: number;
}

/**
 * The body of the 422 returned when an uploaded video breaks the spec
 * (ADR 0009). The browser shows `violations` and links to `presetUrl`.
 */
export interface VideoRejection {
  error: string;
  message: string;
  presetUrl: string;
  statusCode: 422;
  violations: VideoViolation[];
}

/** A Lesson's current WebVTT caption track as the authoring editor shows it. */
export interface AuthoringCaptions {
  cueCount: number;
  language: string;
  trackId: string;
}

/**
 * The body of the 422 returned when an uploaded caption file is not valid
 * WebVTT. The browser shows `issues`.
 */
export interface CaptionRejection {
  error: string;
  issues: CaptionIssue[];
  message: string;
  statusCode: 422;
}

/**
 *
 */
export type AuthoringItemType =
  | "lesson"
  | "knowledge_check"
  | "written_response";

/** An Item in the authoring outline. */
export interface AuthoringItem {
  /** The Lesson's current captions; null until a valid file is uploaded. */
  captions: AuthoringCaptions | null;
  chapterId: string;
  freePreview: boolean;
  id: string;
  keyPoints: string[];
  /** A Knowledge Check's questions with answer keys; null for a Lesson. */
  knowledgeCheck: KnowledgeCheckContent | null;
  learningObjective: string;
  position: number;
  title: string;
  type: AuthoringItemType;
  /** The Lesson's current video; null until one passes validation. */
  video: AuthoringVideo | null;
  /** A newer upload that is not playable yet (sending, converting or failed). */
  videoUpload: AuthoringVideoUpload | null;
  /** A Written Response's prompt and Model Answer; null for other Items. */
  writtenResponse: WrittenResponseContent | null;
}

/**
 *
 */
export interface AuthoringChapter {
  id: string;
  items: AuthoringItem[];
  position: number;
  title: string;
}

/** Why a draft Course cannot be published yet. */
export type PublishReadinessKey =
  | "title"
  | "slug"
  | "cover"
  | "price"
  | "lesson"
  | "freePreviewLesson"
  | "lessonVideo"
  | "lessonCaptions"
  | "knowledgeCheck"
  | "writtenResponse"
  | "testRole";

/** Why one Written Response cannot be published yet. */
export type WrittenResponseProblem = "prompt" | "modelAnswer" | "minLength";

/**
 *
 */
export interface WrittenResponseIssue {
  id: string;
  problems: WrittenResponseProblem[];
  title: string;
}

/** Why one Knowledge Check cannot be published yet. */
export type KnowledgeCheckProblem =
  | "noQuestions"
  | "tooManyQuestions"
  | "passMark"
  | "prompt"
  | "optionCount"
  | "optionText"
  | "optionExplanation"
  | "singleCorrect"
  | "someCorrect"
  | "trueFalseOptions";

/**
 *
 */
export interface KnowledgeCheckIssue {
  id: string;
  problems: KnowledgeCheckProblem[];
  title: string;
}

/**
 *
 */
export interface PublishReadinessIssue {
  key: PublishReadinessKey;
  /** For `knowledgeCheck`: every Knowledge Check that is not ready, and why. */
  checks?: KnowledgeCheckIssue[];
  /** For `writtenResponse`: every Written Response that is not ready, and why. */
  responses?: WrittenResponseIssue[];
  /** For `testRole`: the roles flagged on more than one Knowledge Check. */
  testRoles?: TestRole[];
  /** For `lessonVideo` and `lessonCaptions`: every Lesson that lacks one. */
  lessons?: { id: string; title: string }[];
  /** English text for logs and API clients; the dashboard translates by key. */
  message: string;
}

/** The publish checklist: `missing` names every unmet requirement. */
export interface PublishReadiness {
  missing: PublishReadinessIssue[];
  ready: boolean;
}

/**
 *
 */
export interface CurrentReleaseInfo {
  id: string;
  number: number;
  publishedAt: string;
}

/** One published Release in `GET /v1/academy/courses/:courseId/releases`, newest first. */
export interface ReleaseSummary extends CurrentReleaseInfo {
  /** True for the Release Learners currently see. */
  current: boolean;
  /** The user who published it; null for a Release written before this was recorded. */
  publishedBy: string | null;
}

/**
 *
 */
export interface ReleaseList {
  releases: ReleaseSummary[];
}

/**
 *
 */
export interface AuthoringCourse {
  chapters: AuthoringChapter[];
  /** Draft edits not yet in the current Release; 0 when never published. */
  changesSinceRelease: number;
  currentRelease: CurrentReleaseInfo | null;
  readiness: PublishReadiness;
  /** One of `ACADEMY_CATEGORIES`. */
  category: string;
  cover: string | null;
  currency: string;
  description: string;
  id: string;
  /** One of `ACADEMY_LANGUAGES`. */
  language: string;
  /** One of `ACADEMY_LEVELS`. */
  level: string;
  outcomes: string[];
  price: string;
  publisherOrgId: string;
  audience: string[];
  requirements: string[];
  subtitle: string;
  slug: string;
  status: "draft" | "published" | "archived";
  title: string;
}

/**
 *
 */
export interface AuthoringCourseSummary {
  currency: string;
  id: string;
  price: string;
  publisherOrgId: string;
  slug: string;
  status: "draft" | "published" | "archived";
  title: string;
}

/** A Course detail that differs from the live Release. */
export type CourseChangeField =
  | "title"
  | "slug"
  | "category"
  | "cover"
  | "description"
  | "subtitle"
  | "level"
  | "language"
  | "outcomes"
  | "requirements"
  | "audience"
  | "price"
  | "currency";

/** Which part of one Item differs from the live Release. */
export type ItemChangeField =
  | "title"
  | "objective"
  | "keyPoints"
  | "freePreview"
  | "video"
  | "poster"
  | "captions"
  | "questions"
  | "passMark"
  | "testRole"
  | "prompt"
  | "modelAnswer"
  | "minLength";

/** The kinds of Item, as the change log names them. */
export type ChangeItemType = "lesson" | "knowledge_check" | "written_response";

/**
 * One difference between the draft and the live Release. The list is what the
 * "Changes since version N" card shows, and its length is `changesSinceRelease`.
 */
export type DraftChange =
  | { kind: "course"; field: CourseChangeField }
  | { kind: "chapterAdded"; chapterId: string; title: string }
  | { kind: "chapterRemoved"; chapterId: string; title: string }
  | { kind: "chapterRenamed"; chapterId: string; from: string; to: string }
  | { kind: "chaptersReordered" }
  | {
      kind: "itemAdded" | "itemRemoved";
      chapterTitle: string;
      itemId: string;
      itemType: ChangeItemType;
      title: string;
    }
  | {
      kind: "itemMoved";
      from: string;
      itemId: string;
      itemType: ChangeItemType;
      title: string;
      to: string;
    }
  | {
      kind: "itemChanged";
      chapterTitle: string;
      fields: ItemChangeField[];
      itemId: string;
      itemType: ChangeItemType;
      title: string;
    }
  | { kind: "itemsReordered"; chapterId: string; chapterTitle: string };

/** `GET /v1/academy/courses/:courseId/changes`: the draft's differences from the live Release. */
export interface DraftChanges {
  changes: DraftChange[];
  /** When the live Release was published (ISO 8601); null when never published. */
  publishedAt: string | null;
  /** The live Release's number; null when never published. */
  releaseNumber: number | null;
}

/**
 * What a Release freezes (ADR 0010). Item ids are the draft Item ids, so the
 * same Item has the same id in every Release of its Course. Chapter and Item
 * order is array order.
 */
/** The video a Lesson used when the Release was published. */
export interface ReleaseVideo {
  assetId: string;
  audioCodec: string | null;
  bitrateBps: number;
  durationMs: number;
  height: number;
  posterKey: string | null;
  sizeBytes: number;
  videoCodec: string;
  width: number;
}

/** A caption track a Lesson used when the Release was published. */
export interface ReleaseCaption {
  cueCount: number;
  language: string;
  trackId: string;
}

/**
 *
 */
export interface ReleaseSnapshotLesson {
  /** Always empty or one track today; an array so more languages can follow. */
  captions: ReleaseCaption[];
  freePreview: boolean;
  id: string;
  keyPoints: string[];
  learningObjective: string;
  title: string;
  type: "lesson";
  /** Null only in Releases written before video was required (ticket 06). */
  video: ReleaseVideo | null;
}

/** A Knowledge Check as a Release froze it, answer keys included. */
export interface ReleaseSnapshotKnowledgeCheck {
  /** Never a Free Preview; present so every Item answers the access check. */
  freePreview: boolean;
  id: string;
  learningObjective: string;
  passMark: number;
  questions: KnowledgeCheckQuestion[];
  /** Present only on the Course's Pre-test or Post-test. */
  testRole?: TestRole;
  title: string;
  type: "knowledge_check";
}

/** A Written Response as a Release froze it, Model Answer included. */
export interface ReleaseSnapshotWrittenResponse {
  /** Never a Free Preview; present so every Item answers the access check. */
  freePreview: boolean;
  id: string;
  learningObjective: string;
  minLength: number;
  modelAnswer: string;
  prompt: string;
  title: string;
  type: "written_response";
}

/** Every Item kind a Release can hold. Later Activities add members here. */
export type ReleaseSnapshotItem =
  | ReleaseSnapshotLesson
  | ReleaseSnapshotKnowledgeCheck
  | ReleaseSnapshotWrittenResponse;

/**
 *
 */
export interface ReleaseSnapshotChapter {
  id: string;
  items: ReleaseSnapshotItem[];
  title: string;
}

/**
 *
 */
export interface ReleaseSnapshot {
  chapters: ReleaseSnapshotChapter[];
  course: {
    /** Absent on Releases published before categories; read it as `other`. */
    category?: string;
    cover: string | null;
    currency: string;
    description: string;
    /** Absent on Releases published before course details; read it as `bn`. */
    language?: string;
    /** Absent on Releases published before course details; read it as `all`. */
    level?: string;
    outcomes: string[];
    price: string;
    slug: string;
    /** Absent on Releases published before course details; read it as empty. */
    subtitle?: string;
    title: string;
    audience?: string[];
    requirements?: string[];
  };
}

/**
 * One Course in the learning-gain report. Gain is in percentage points, from a
 * Learner's first Pre-test attempt to their best Post-test attempt, averaged
 * over the Learners who have both (`sampleSize`). Null when nobody has both.
 */
export interface LearningGainCourse {
  /** Mean Post-test (best attempt) score of the sampled Learners, 0 to 100. */
  averagePostScore: number | null;
  /** Mean Pre-test (first attempt) score of the sampled Learners, 0 to 100. */
  averagePreScore: number | null;
  /** Mean of Post minus Pre over the sampled Learners, in percentage points. */
  averageGain: number | null;
  courseId: string;
  /** Learners with a Pre-test attempt followed by a Post-test attempt. */
  sampleSize: number;
  slug: string;
  title: string;
}

/** `GET /v1/academy/reports/learning-gain`: gain per Course, apart from engagement. */
export interface LearningGainReport {
  courses: LearningGainCourse[];
}

/**
 * One Item of the current Release in the engagement report. A Learner is
 * counted once they have touched this Item or any later one in feed order
 * (`reached`): someone who never got that far neither skipped nor completed
 * it. Rates are percentages of `reached`, or null while `reached` is 0.
 *
 * - Started: the Item is Started or Completed (an Activity opened counts once
 *   it has an attempt or a Completed state).
 * - Completed: the Item's completion rule is met.
 * - Skipped: not Started, but a later Item is (the glossary's "scrolled past
 *   without Starting it", read from data since scrolling sends nothing).
 */
export interface EngagementItem {
  /** Learners who answered "No" to "Was this clear?" (Lessons only). */
  clearNo: number;
  /** Learners who answered "Yes" to "Was this clear?" (Lessons only). */
  clearYes: number;
  completed: number;
  completionRate: number | null;
  itemId: string;
  reached: number;
  skipRate: number | null;
  skipped: number;
  started: number;
  title: string;
  type: "lesson" | "knowledge_check" | "written_response";
}

/**
 *
 */
export interface EngagementCourse {
  chapters: { chapterId: string; items: EngagementItem[]; title: string }[];
  courseId: string;
  /** Learners with any progress row in this Course. */
  learners: number;
  slug: string;
  title: string;
}

/**
 * `GET /v1/academy/reports/engagement`: how Learners move through each Course.
 * Engagement only; it says nothing about whether anyone learned (see the
 * learning-gain and question-correctness reports).
 */
export interface EngagementReport {
  courses: EngagementCourse[];
}

/** One question in the question-correctness report. */
export interface QuestionCorrectness {
  /** Learners whose first attempt on this Knowledge Check answered it right. */
  correct: number;
  /** `correct` over `firstAttempts` as a percentage; null with no attempts. */
  correctRate: number | null;
  /** Learners who made a first attempt at this question's Knowledge Check. */
  firstAttempts: number;
  prompt: string;
  questionId: string;
}

/**
 *
 */
export interface QuestionCorrectnessCheck {
  itemId: string;
  questions: QuestionCorrectness[];
  /** Set for the Course's Pre-test or Post-test. */
  testRole: TestRole | null;
  title: string;
}

/**
 *
 */
export interface QuestionCorrectnessCourse {
  checks: QuestionCorrectnessCheck[];
  courseId: string;
  slug: string;
  title: string;
}

/**
 * `GET /v1/academy/reports/question-correctness`: for each Knowledge Check
 * question of the current Release, how many Learners got it right on their
 * FIRST attempt (retries are unlimited, so later attempts say nothing about
 * the question's difficulty). A learning measure, kept apart from engagement.
 */
export interface QuestionCorrectnessReport {
  courses: QuestionCorrectnessCourse[];
}

/** The result of `POST /v1/academy/courses/:courseId/publish`. */
export interface PublishResult {
  course: AuthoringCourse;
  release: CurrentReleaseInfo;
}

/** One Item on the public Course page: titles only, no lesson content. */
export const publicCourseItemSchema = z.object({
  id: z.string(),
  type: z.enum(["lesson", "knowledge_check", "written_response"]),
  title: z.string(),
  freePreview: z.boolean(),
  /** Lesson length from the Release; null for a Knowledge Check or no video. */
  durationSeconds: z.number().nullable(),
});

export const publicCourseChapterSchema = z.object({
  id: z.string(),
  title: z.string(),
  items: z.array(publicCourseItemSchema),
});

/** A published Course as its public page shows it (`GET /v1/courses/:slug`). */
export const publicCourseSchema = catalogCourseSchema.extend({
  instructor: catalogInstructorSchema.extend({
    bio: z.string().nullable(),
    links: instructorLinksSchema,
    /** Published Courses this instructor has. */
    courseCount: z.number().int().nonnegative(),
  }),
  language: academyLanguageSchema,
  requirements: z.array(z.string()),
  audience: z.array(z.string()),
  /** Active Learners across free and paid access. */
  studentCount: z.number().int().nonnegative(),
  /** Ratings per star, five stars first: `[n5, n4, n3, n2, n1]`. */
  ratingBreakdown: z.array(z.number().int().nonnegative()).length(5).default([0, 0, 0, 0, 0]),
  /** When the current Release was published (ISO 8601). */
  publishedAt: z.string(),
  releaseNumber: z.number().int().positive(),
  chapters: z.array(publicCourseChapterSchema),
});

/**
 *
 */
export type PublicCourse = z.infer<typeof publicCourseSchema>;

/**
 * An instructor's public page (`GET /v1/instructors/:id`): their profile, totals
 * across their published Courses, and those Courses as Catalog cards.
 */
export const publicInstructorSchema = z.object({
  /** The instructor's Publisher id. */
  id: z.string(),
  name: z.string(),
  headline: z.string().nullable(),
  bio: z.string().nullable(),
  photoUrl: z.string().nullable(),
  links: instructorLinksSchema,
  courseCount: z.number().int().nonnegative(),
  /** Active Learners summed over their Courses (one person in two Courses counts twice). */
  studentCount: z.number().int().nonnegative(),
  lessonCount: z.number().int().nonnegative(),
  totalDurationSeconds: z.number().int().nonnegative(),
  /** Mean rating over every rating on their Courses (not a mean of means). */
  ratingAverage: z.number().min(1).max(5).nullable().default(null),
  ratingCount: z.number().int().nonnegative().default(0),
  courses: z.array(catalogCourseSchema),
});

/**
 *
 */
export type PublicInstructor = z.infer<typeof publicInstructorSchema>;

/** Most courses a search suggestion list returns. */
export const SEARCH_SUGGESTION_COURSES = 5;

/** Most instructors a search suggestion list returns. */
export const SEARCH_SUGGESTION_INSTRUCTORS = 3;

/**
 * What the header search suggests while someone types
 * (`GET /v1/search/suggestions?q=`): the best matching Courses and the
 * instructors whose name matches. Topics are matched in the browser against
 * the translated category names.
 */
export const searchSuggestionsSchema = z.object({
  courses: z.array(
    z.object({
      slug: z.string(),
      title: z.string(),
      coverUrl: z.string().nullable(),
      category: academyCategorySchema,
      instructorName: z.string(),
    })
  ),
  instructors: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      headline: z.string().nullable(),
      photoUrl: z.string().nullable(),
      courseCount: z.number().int().nonnegative(),
    })
  ),
});

/**
 *
 */
export type SearchSuggestions = z.infer<typeof searchSuggestionsSchema>;

/**
 * Seconds a playback link stays valid (ADR 0009: about 30 minutes). The client
 * asks for a fresh one when it expires, and resumes at the current time.
 */
export const PLAYBACK_LINK_SECONDS = 1800;

/** One caption track the API serves itself, so playback needs no bucket CORS. */
export const playbackCaptionSchema = z.object({
  language: z.string(),
  /** Same-origin API path of the WebVTT file. */
  src: z.string(),
});

/** Longest written review. */
export const COURSE_REVIEW_MAX_LENGTH = 1000;

/** Reviews per page of the public list. */
export const COURSE_REVIEWS_PAGE_SIZE = 10;

/**
 * `PUT /v1/courses/:slug/rating`. Leaving `review` out keeps the text already
 * written; `null` or blank text removes it.
 */
export const rateCourseSchema = z
  .object({
    rating: z.number().int().min(1).max(5),
    review: z
      .string()
      .trim()
      .max(COURSE_REVIEW_MAX_LENGTH)
      .transform((text) => (text === "" ? null : text))
      .nullable()
      .optional(),
  })
  .strict();

/**
 *
 */
export type RateCourseInput = z.infer<typeof rateCourseSchema>;

/** A Learner's own rating of a Course. */
export const courseRatingSchema = z
  .object({
    rating: z.number().int().min(1).max(5),
    review: z.string().nullable(),
    /** ISO time the rating was last saved. */
    updatedAt: z.string(),
  })
  .strict();

/**
 *
 */
export type CourseRating = z.infer<typeof courseRatingSchema>;

/**
 * `GET /v1/courses/:slug/reviews`: ratings that came with written text, newest
 * first. Learners show as a first name and an initial.
 */
export const courseReviewsSchema = z.object({
  reviews: z.array(
    z.object({
      id: z.string(),
      rating: z.number().int().min(1).max(5),
      review: z.string(),
      learnerName: z.string(),
      learnerPhotoUrl: z.string().nullable(),
      updatedAt: z.string(),
    })
  ),
  /** Pass back as `?cursor=` for the next page; null on the last page. */
  nextCursor: z.string().nullable(),
});

/**
 *
 */
export type CourseReviews = z.infer<typeof courseReviewsSchema>;

/**
 * The shape of a Lesson's converted video, so the player can size its frame:
 * 1080x1920 for a vertical lesson, 1920x1080 for a horizontal one.
 */
export const lessonAspectSchema = z.object({
  width: z.number().int().positive(),
  height: z.number().int().positive(),
});

/**
 *
 */
export type LessonAspect = z.infer<typeof lessonAspectSchema>;

/**
 * A short-lived link to a Lesson's video (`GET /v1/courses/:slug/items/:itemId/playback`).
 * Issued only after the access decision allows it.
 */
export const playbackLinkSchema = z.object({
  url: z.string(),
  /** ISO time the `url` stops working. */
  expiresAt: z.string(),
  posterUrl: z.string().nullable(),
  durationSeconds: z.number(),
  /** The converted video's size; null for links issued before it was recorded. */
  aspect: lessonAspectSchema.nullable().default(null),
  captions: z.array(playbackCaptionSchema),
});

/**
 *
 */
export type PlaybackCaption = z.infer<typeof playbackCaptionSchema>;

/**
 *
 */
export type PlaybackLink = z.infer<typeof playbackLinkSchema>;

/**
 * One Lesson in the learn feed. Strict on purpose: the learn payload is built
 * by naming each field (never by spreading a Release Item), and parsing with a
 * strict schema rejects any extra field, so an answer key added to a future
 * Item type cannot ride along unnoticed.
 */
export const learnLessonSchema = z
  .object({
    id: z.string(),
    type: z.literal("lesson"),
    chapterId: z.string(),
    title: z.string(),
    learningObjective: z.string(),
    keyPoints: z.array(z.string()),
    /** Lesson length from the Release; null when the Release has no video. */
    durationSeconds: z.number().nullable(),
    hasVideo: z.boolean(),
    /** Shape of the Lesson's video; null without a video or in older payloads. */
    aspect: lessonAspectSchema.nullable().default(null),
  })
  .strict();

/** An option as a Learner sees it before answering: no key, no explanation. */
export const learnOptionSchema = z
  .object({ id: z.string(), text: z.string() })
  .strict();

export const learnQuestionSchema = z
  .object({
    id: z.string(),
    kind: questionKindSchema,
    prompt: z.string(),
    options: z.array(learnOptionSchema),
  })
  .strict();

/**
 * A Knowledge Check in the learn feed. It carries the questions and the Pass
 * Mark but never which option is correct or why: that only comes back after an
 * attempt, as feedback.
 */
export const learnKnowledgeCheckSchema = z
  .object({
    id: z.string(),
    type: z.literal("knowledge_check"),
    chapterId: z.string(),
    title: z.string(),
    learningObjective: z.string(),
    passMark: z.number().int(),
    questions: z.array(learnQuestionSchema),
  })
  .strict();

/**
 * A Written Response in the learn feed: the prompt and the minimum length,
 * never the Model Answer. That only comes back with a submission.
 */
export const learnWrittenResponseSchema = z
  .object({
    id: z.string(),
    type: z.literal("written_response"),
    chapterId: z.string(),
    title: z.string(),
    learningObjective: z.string(),
    prompt: z.string(),
    minLength: z.number().int(),
  })
  .strict();

/** Every Item kind the feed can show. Activities add members later, never keys. */
export const learnItemSchema = z.discriminatedUnion("type", [
  learnLessonSchema,
  learnKnowledgeCheckSchema,
  learnWrittenResponseSchema,
]);

/**
 * The Pre-test or Post-test of a Course, offered apart from the Lessons: the
 * feed's chapters never contain it, it counts toward neither Course Completion
 * nor the percent, and it is taken through the ordinary attempts route. It
 * carries the Learner's own attempt count and best score so the offer can say
 * "not taken yet" and the completion screen can show a result.
 */
export const learnTestSchema = z
  .object({
    role: testRoleSchema,
    item: learnKnowledgeCheckSchema,
    attempts: z.number().int().nonnegative(),
    bestScorePercent: z.number().int().min(0).max(100).nullable(),
  })
  .strict();

export const learnChapterSchema = z
  .object({
    id: z.string(),
    title: z.string(),
    items: z.array(learnItemSchema),
  })
  .strict();

/**
 * One Item's progress for the signed-in Learner (spec: "Item progress").
 * Items the Learner never touched are simply absent from the list.
 */
export const itemProgressSchema = z
  .object({
    itemId: z.string(),
    state: z.enum(["not_started", "started", "completed"]),
    playedSeconds: z.number(),
    positionSeconds: z.number(),
    completedAt: z.string().nullable(),
    completionMethod: z.string().nullable(),
    /** Best Knowledge Check score so far; absent or null for other Items. */
    bestScorePercent: z.number().int().min(0).max(100).nullable().optional(),
    /** Knowledge Check attempts made; absent or 0 for other Items. */
    attempts: z.number().int().nonnegative().optional(),
  })
  .strict();

/** Where Continue takes the Learner: an Item and a position inside it. */
export const resumePointSchema = z
  .object({ itemId: z.string(), positionSeconds: z.number() })
  .strict();

/** The Learner's progress through the Course's current Release. */
export const courseProgressSchema = z
  .object({
    items: z.array(itemProgressSchema),
    /** Required Items of the current Release that are Completed. */
    completedItems: z.number().int().nonnegative(),
    totalItems: z.number().int().nonnegative(),
    /** `completedItems` over `totalItems`, rounded down to a whole percent. */
    percent: z.number().int().min(0).max(100),
    /** First Course Completion time; never cleared once recorded. */
    completedAt: z.string().nullable(),
    resume: resumePointSchema.nullable(),
  })
  .strict();

/** `GET /v1/courses/:slug/learn`: the current Release for an entitled Learner. */
export const learnPayloadSchema = z
  .object({
    courseId: z.string(),
    slug: z.string(),
    title: z.string(),
    releaseNumber: z.number().int().positive(),
    chapters: z.array(learnChapterSchema),
    /** The Pre-test and Post-test, when the Release has them (at most one each). */
    tests: z.array(learnTestSchema).optional(),
    /**
     * Items added to the Course after this Learner's access began, not yet
     * Started: the outline marks them "new". Absent in older payloads.
     */
    newItemIds: z.array(z.string()).optional(),
    /**
     * The Learner's own "Was this clear?" answers, by Lesson id: true for yes,
     * false for no. A Lesson they never answered is absent. Absent in older
     * payloads.
     */
    clarity: z.record(z.string(), z.boolean()).optional(),
    /** The Learner's own rating of the Course, or null before they rated it. */
    myRating: courseRatingSchema.nullable().default(null),
    progress: courseProgressSchema,
  })
  .strict();

/**
 *
 */
export type LearnLesson = z.infer<typeof learnLessonSchema>;

/**
 *
 */
export type LearnKnowledgeCheck = z.infer<typeof learnKnowledgeCheckSchema>;

/**
 *
 */
export type LearnQuestion = z.infer<typeof learnQuestionSchema>;

/**
 *
 */
export type LearnWrittenResponse = z.infer<typeof learnWrittenResponseSchema>;

/**
 *
 */
export type LearnItem = z.infer<typeof learnItemSchema>;

/**
 *
 */
export type LearnTest = z.infer<typeof learnTestSchema>;

/**
 *
 */
export type LearnChapter = z.infer<typeof learnChapterSchema>;

/**
 *
 */
export type LearnPayload = z.infer<typeof learnPayloadSchema>;

/**
 *
 */
export type ItemProgress = z.infer<typeof itemProgressSchema>;

/**
 *
 */
export type ResumePoint = z.infer<typeof resumePointSchema>;

/**
 *
 */
export type CourseProgress = z.infer<typeof courseProgressSchema>;

/**
 * One thing the player observed. `time` is a periodic report of played time
 * and position, `ended` means playback reached the end, `key-points` is the
 * "I read the key points" action. Scrolling past an Item sends nothing.
 */
export const progressEventSchema = z.object({
  itemId: z.string().min(1).max(100),
  type: z.enum(["time", "ended", "key-points"]),
  /** Seconds actually played so far, seeks excluded (the client's total). */
  playedSeconds: z.number().finite().min(0).max(86_400),
  positionSeconds: z.number().finite().min(0).max(86_400),
  /** Client clock, epoch milliseconds. The server never trusts it past "now". */
  clientTime: z.number().int().min(0),
  /** Unique per client; a repeated sequence number is ignored. */
  sequence: z.number().int().min(0).max(2_147_483_647),
});

/** `POST /v1/courses/:slug/progress`. */
export const progressBatchSchema = z.object({
  /** A random id for this browser or device; scopes the sequence numbers. */
  clientId: z.string().min(8).max(64),
  events: z.array(progressEventSchema).min(1).max(200),
});

/**
 *
 */
export type ProgressEvent = z.infer<typeof progressEventSchema>;

/**
 *
 */
export type ProgressBatch = z.infer<typeof progressBatchSchema>;

/** The answer to a progress batch, with the merged result. */
export const progressResultSchema = z
  .object({
    /** Events applied. */
    accepted: z.number().int().nonnegative(),
    /** Events dropped because their sequence number was seen before. */
    duplicates: z.number().int().nonnegative(),
    /** Events for Items that are not in the current Release. */
    ignored: z.number().int().nonnegative(),
    progress: courseProgressSchema,
  })
  .strict();

/**
 *
 */
export type ProgressResult = z.infer<typeof progressResultSchema>;

/**
 * `PUT /v1/courses/:slug/items/:itemId/clarity`: the one-tap "Was this
 * clear?" answer for a Lesson. `clear` true is yes, false is no. One answer per
 * Learner and Lesson: sending it again replaces the earlier answer.
 */
export const setClaritySchema = z.object({ clear: z.boolean() }).strict();

/**
 *
 */
export type SetClarityInput = z.infer<typeof setClaritySchema>;

/** The stored answer, echoed back. */
export const clarityResultSchema = z
  .object({ itemId: z.string(), clear: z.boolean() })
  .strict();

/**
 *
 */
export type ClarityResult = z.infer<typeof clarityResultSchema>;

const MAX_ANSWERED_QUESTIONS = 20;

/**
 * `POST /v1/courses/:slug/items/:itemId/attempts`. `answers` maps every
 * question id to the option ids the Learner selected. `clientAttemptId` is
 * made once per press of Check and kept across retries, so a resend after a
 * lost response is recognised and never counts twice. `releaseNumber` is the
 * Release the Learner's page was loaded from; when it is no longer current and
 * the questions have changed, the answers are refused as stale (409) instead of
 * "incomplete" (400), so the client reloads the new questions.
 */
export const submitCourseAttemptSchema = z.object({
  clientAttemptId: z.string().min(8).max(64),
  releaseNumber: z.number().int().positive().optional(),
  answers: z
    .record(
      z.string().min(1).max(100),
      z.array(z.string().min(1).max(100)).max(10)
    )
    .refine((value) => Object.keys(value).length <= MAX_ANSWERED_QUESTIONS, {
      message: "Too many answers",
    }),
});

/**
 *
 */
export type SubmitCourseAttemptInput = z.infer<
  typeof submitCourseAttemptSchema
>;

/** One option after scoring: whether it was right, picked, and why. */
export const attemptOptionFeedbackSchema = z
  .object({
    id: z.string(),
    text: z.string(),
    correct: z.boolean(),
    selected: z.boolean(),
    explanation: z.string(),
  })
  .strict();

export const attemptQuestionFeedbackSchema = z
  .object({
    id: z.string(),
    kind: questionKindSchema,
    prompt: z.string(),
    /** The question counts as correct only when the selection is exactly the key. */
    correct: z.boolean(),
    options: z.array(attemptOptionFeedbackSchema),
  })
  .strict();

/** The scored attempt, with feedback on every option and the merged progress. */
export const attemptResultSchema = z
  .object({
    attemptId: z.string(),
    itemId: z.string(),
    releaseNumber: z.number().int().positive(),
    correctCount: z.number().int().nonnegative(),
    totalQuestions: z.number().int().nonnegative(),
    scorePercent: z.number().int().min(0).max(100),
    passMark: z.number().int(),
    passed: z.boolean(),
    /** Best score over every attempt, this one included. */
    bestScorePercent: z.number().int().min(0).max(100),
    /** Attempts made so far, this one included. */
    attempts: z.number().int().positive(),
    /** True when this attempt id was seen before and the stored result is returned. */
    replay: z.boolean(),
    questions: z.array(attemptQuestionFeedbackSchema),
    progress: courseProgressSchema,
  })
  .strict();

/**
 *
 */
export type AttemptOptionFeedback = z.infer<typeof attemptOptionFeedbackSchema>;

/**
 *
 */
export type AttemptQuestionFeedback = z.infer<
  typeof attemptQuestionFeedbackSchema
>;

/**
 *
 */
export type AttemptResult = z.infer<typeof attemptResultSchema>;

/**
 * `POST /v1/courses/:slug/items/:itemId/responses`. `clientSubmissionId` is
 * made once per press of Submit and kept across retries, so a resend after a
 * lost response is recognised and never stored twice. The body is measured
 * after trimming; the server refuses one shorter than the minimum length.
 */
export const submitResponseSchema = z.object({
  clientSubmissionId: z.string().min(8).max(64),
  body: z.string().max(WRITTEN_RESPONSE_BODY_MAX),
});

/**
 *
 */
export type SubmitResponseInput = z.infer<typeof submitResponseSchema>;

/**
 * A stored Written Response with the Model Answer, the only place the Model
 * Answer leaves the API. Also what `GET .../responses` returns (with `replay`
 * true) so a Learner who comes back still sees what they wrote and the Model
 * Answer.
 */
export const responseResultSchema = z
  .object({
    responseId: z.string(),
    itemId: z.string(),
    releaseNumber: z.number().int().positive(),
    body: z.string(),
    submittedAt: z.string(),
    minLength: z.number().int(),
    modelAnswer: z.string(),
    /** True when this submission id was seen before and the stored response is returned. */
    replay: z.boolean(),
    progress: courseProgressSchema,
  })
  .strict();

/**
 *
 */
export type ResponseResult = z.infer<typeof responseResultSchema>;

/** `POST /v1/courses/:slug/enroll`: the Learner's Entitlement after enrolling. */
export const courseEnrollmentSchema = z.object({
  courseId: z.string(),
  slug: z.string(),
  status: z.literal("active"),
  source: z.enum(["purchase", "free", "staff"]),
});

/**
 *
 */
export type CourseEnrollment = z.infer<typeof courseEnrollmentSchema>;

/** One Course in a Learner's Library. */
export const libraryCourseSchema = z.object({
  id: z.string(),
  slug: z.string(),
  title: z.string(),
  cover: z.string().nullable(),
  coverUrl: z.string().nullable(),
  instructor: z.object({
    /** The instructor's Publisher id, for a link to their page; empty without a profile. */
    id: z.string().default(""),
    name: z.string(),
    photoUrl: z.string().nullable().default(null),
  }),
  description: z.string(),
  category: academyCategorySchema.default("other"),
  lessonCount: z.number().int().nonnegative().default(0),
  totalDurationSeconds: z.number().int().nonnegative().default(0),
  /** ISO time the Learner's progress last changed, or null before they started. */
  lastActivityAt: z.string().nullable().default(null),
  /** Title of the Item Continue opens, or null when the Course has none. */
  nextItemTitle: z.string().nullable().default(null),
  /** The Learner's own rating of the Course, or null before they rated it. */
  myRating: courseRatingSchema.nullable().default(null),
  /** ISO time the Entitlement was granted. */
  enrolledAt: z.string(),
  progress: z.object({
    completedItems: z.number().int().nonnegative(),
    totalItems: z.number().int().nonnegative(),
    percent: z.number().int().min(0).max(100),
    completedAt: z.string().nullable(),
  }),
});

/** `GET /v1/library`: the Learner's Courses, most recently enrolled first. */
export const libraryResponseSchema = z.object({
  courses: z.array(libraryCourseSchema),
});

/**
 *
 */
export type LibraryCourse = z.infer<typeof libraryCourseSchema>;

/**
 *
 */
export type LibraryResponse = z.infer<typeof libraryResponseSchema>;

/** Locales a checkout can return the Learner to (the Academy's own). */
export const checkoutLocaleSchema = z.enum(["en-US", "bn-BD"]);

/**
 * `POST /v1/courses/:slug/checkout`. The body carries only the return locale:
 * the price always comes from the Course's current Release, never the request.
 */
export const checkoutRequestSchema = z
  .object({ locale: checkoutLocaleSchema.optional() })
  .strict();

/**
 *
 */
export type CheckoutRequest = z.infer<typeof checkoutRequestSchema>;

/** The pending Purchase and the SSLCommerz page to send the Learner to. */
export const checkoutResponseSchema = z.object({
  purchaseId: z.string(),
  gatewayUrl: z.string(),
  /** True when an open pending Purchase from the last 30 minutes was continued. */
  reused: z.boolean(),
});

/**
 *
 */
export type CheckoutResponse = z.infer<typeof checkoutResponseSchema>;

/**
 * Body of `POST /v1/purchases/:id/mock-pay`, the test checkout used while
 * `ACADEMY_PAYMENT_MODE=mock`. No gateway is involved and no money moves.
 */
export const mockPayRequestSchema = z.object({
  outcome: z.enum(["paid", "cancelled", "failed"]),
});

/**
 *
 */
export type MockPayRequest = z.infer<typeof mockPayRequestSchema>;

/** Where a Purchase stands, as the Learner may see it. */
export const purchaseStatusValueSchema = z.enum([
  "pending",
  "paid",
  "failed",
  "cancelled",
  "expired",
  "failed_validation",
  "refunded",
]);

/**
 * `GET /v1/purchases/:id`, polled by the payment result page. `reference` is
 * the number the Learner quotes to support (the Purchase id).
 */
export const purchaseStatusSchema = z.object({
  id: z.string(),
  reference: z.string(),
  status: purchaseStatusValueSchema,
  amount: z.string(),
  currency: z.string(),
  /** Null when the Course is no longer published. */
  courseSlug: z.string().nullable(),
  courseTitle: z.string().nullable(),
  paidAt: z.string().nullable(),
});

/**
 *
 */
export type PurchaseStatus = z.infer<typeof purchaseStatusSchema>;

/**
 * Academy operations (ticket 17): what support staff see when they look for a
 * Purchase. Staff are members of the Publisher organization (ADR 0011), and
 * every row is scoped to the Publishers the actor belongs to.
 */
export const OPS_SEARCH_MAX_LENGTH = 200;

/** `GET /v1/academy/purchases?q=&status=`; `q` is an email, phone or transaction id. */
export const opsPurchaseSearchSchema = z.object({
  q: z.string().trim().max(OPS_SEARCH_MAX_LENGTH).optional(),
  status: purchaseStatusValueSchema.optional(),
});

/**
 *
 */
export type OpsPurchaseSearch = z.infer<typeof opsPurchaseSearchSchema>;

export const opsPurchaseSummarySchema = z.object({
  /** Also the SSLCommerz transaction id. */
  id: z.string(),
  status: purchaseStatusValueSchema,
  amount: z.string(),
  currency: z.string(),
  duplicate: z.boolean(),
  courseId: z.string(),
  courseTitle: z.string(),
  learnerId: z.string(),
  learnerName: z.string(),
  learnerEmail: z.string(),
  learnerPhone: z.string().nullable(),
  createdAt: z.string(),
  paidAt: z.string().nullable(),
});

/**
 *
 */
export type OpsPurchaseSummary = z.infer<typeof opsPurchaseSummarySchema>;

export const opsPurchaseSearchResponseSchema = z.object({
  purchases: z.array(opsPurchaseSummarySchema),
  /** True when more matches exist than were returned; narrow the search. */
  truncated: z.boolean(),
});

/**
 *
 */
export type OpsPurchaseSearchResponse = z.infer<
  typeof opsPurchaseSearchResponseSchema
>;

export const opsPurchaseEventSchema = z.object({
  id: z.string(),
  source: z.enum([
    "checkout",
    "ipn",
    "callback",
    "reconciliation",
    "staff",
    "mock",
  ]),
  outcome: z.string(),
  fromStatus: purchaseStatusValueSchema.nullable(),
  toStatus: purchaseStatusValueSchema.nullable(),
  payloadHash: z.string().nullable(),
  /** The staff member behind a `staff` event; null for the gateway and jobs. */
  actorEmail: z.string().nullable(),
  /** The reason staff gave for a grant, revoke or refund mark. */
  reason: z.string().nullable(),
  createdAt: z.string(),
});

/**
 *
 */
export type OpsPurchaseEvent = z.infer<typeof opsPurchaseEventSchema>;

/**
 * The refund rule (spec: "Refunds"): within 7 days of purchase and under 30%
 * progress, or a duplicate charge. When it is not met staff need an override
 * reason to mark the Purchase refunded.
 */
export const refundRuleSchema = z.object({
  windowDays: z.number().int(),
  progressLimitPercent: z.number().int(),
  daysSincePurchase: z.number().int(),
  withinWindow: z.boolean(),
  progressPercent: z.number().int(),
  underProgressLimit: z.boolean(),
  duplicate: z.boolean(),
  refundable: z.boolean(),
  overrideRequired: z.boolean(),
});

/**
 *
 */
export type RefundRule = z.infer<typeof refundRuleSchema>;

/** One Purchase with its whole timeline, oldest event first. */
export const opsPurchaseDetailSchema = opsPurchaseSummarySchema.extend({
  settlementSource: z
    .enum(["ipn", "reconciliation", "staff", "mock"])
    .nullable(),
  gatewayValId: z.string().nullable(),
  gatewayBankTranId: z.string().nullable(),
  receiptSentAt: z.string().nullable(),
  updatedAt: z.string(),
  /** Pending and expired Purchases can be asked about at the gateway. */
  canReconcile: z.boolean(),
  /** A paid Purchase can be marked refunded after the refund was made in the SSLCommerz panel. */
  canMarkRefunded: z.boolean(),
  /** The refund rule as it stands now; null unless the Purchase is paid. */
  refund: refundRuleSchema.nullable(),
  events: z.array(opsPurchaseEventSchema),
});

/**
 *
 */
export type OpsPurchaseDetail = z.infer<typeof opsPurchaseDetailSchema>;

/** What "Reconcile now" found. */
export const reconcileNowOutcomeSchema = z.enum([
  "settled",
  "already_paid",
  "still_pending",
  "rejected",
  "closed",
]);

/**
 *
 */
export type ReconcileNowOutcome = z.infer<typeof reconcileNowOutcomeSchema>;

export const reconcileNowResponseSchema = z.object({
  outcome: reconcileNowOutcomeSchema,
  purchase: opsPurchaseDetailSchema,
});

/**
 *
 */
export type ReconcileNowResponse = z.infer<typeof reconcileNowResponseSchema>;

/** A reason is required for every staff grant, revoke and refund mark, and is audited. */
export const OPS_REASON_MIN_LENGTH = 5;
export const OPS_REASON_MAX_LENGTH = 500;

const opsReasonSchema = z
  .string()
  .trim()
  .min(OPS_REASON_MIN_LENGTH)
  .max(OPS_REASON_MAX_LENGTH);

/**
 * `POST /v1/academy/purchases/:id/refund`: mark a paid Purchase refunded after
 * the refund was made in the SSLCommerz panel. `overrideReason` is required
 * when the refund rule is not met (and the charge is not a duplicate).
 */
export const markRefundedInputSchema = z.object({
  reason: opsReasonSchema,
  overrideReason: opsReasonSchema.optional(),
});

/**
 *
 */
export type MarkRefundedInput = z.infer<typeof markRefundedInputSchema>;

export const markRefundedResponseSchema = z.object({
  purchase: opsPurchaseDetailSchema,
  /** False when the Learner keeps access through another paid Purchase. */
  entitlementRevoked: z.boolean(),
});

/**
 *
 */
export type MarkRefundedResponse = z.infer<typeof markRefundedResponseSchema>;

/** `POST /v1/academy/entitlements`: a staff grant (beta access or an exception). */
export const grantEntitlementInputSchema = z.object({
  learnerId: z.string().trim().min(1).max(OPS_SEARCH_MAX_LENGTH),
  courseId: z.string().trim().min(1).max(OPS_SEARCH_MAX_LENGTH),
  reason: opsReasonSchema,
});

/**
 *
 */
export type GrantEntitlementInput = z.infer<typeof grantEntitlementInputSchema>;

/** `POST /v1/academy/entitlements/:id/revoke`. Progress is kept. */
export const revokeEntitlementInputSchema = z.object({
  reason: opsReasonSchema,
});

/**
 *
 */
export type RevokeEntitlementInput = z.infer<
  typeof revokeEntitlementInputSchema
>;

/** One Entitlement as support sees it, with the Learner's progress in that Course. */
export const opsEntitlementSchema = z.object({
  id: z.string(),
  courseId: z.string(),
  courseTitle: z.string(),
  status: z.enum(["active", "revoked"]),
  source: z.enum(["purchase", "free", "staff"]),
  purchaseId: z.string().nullable(),
  grantedAt: z.string(),
  revokedAt: z.string().nullable(),
  reason: z.string().nullable(),
  /**
   * The refund rule as of now, for an active Entitlement backed by a paid
   * Purchase; null for a free or staff grant, a revoked one, or an
   * Entitlement whose Purchase is no longer paid.
   */
  refund: refundRuleSchema.nullable(),
  progress: z.object({
    completedItems: z.number().int(),
    totalItems: z.number().int(),
    percent: z.number().int(),
    completedAt: z.string().nullable(),
  }),
});

/**
 *
 */
export type OpsEntitlement = z.infer<typeof opsEntitlementSchema>;

/** `GET /v1/academy/learners?q=`; `q` is an email or phone number. */
export const opsLearnerLookupSchema = z.object({
  q: z.string().trim().min(1).max(OPS_SEARCH_MAX_LENGTH),
});

/**
 *
 */
export type OpsLearnerLookup = z.infer<typeof opsLearnerLookupSchema>;

export const opsLearnerSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  phone: z.string().nullable(),
  /** Entitlements to this Publisher's Courses, in any status. */
  entitlements: z.array(opsEntitlementSchema),
});

/**
 *
 */
export type OpsLearner = z.infer<typeof opsLearnerSchema>;

export const opsLearnerLookupResponseSchema = z.object({
  learners: z.array(opsLearnerSchema),
  /** Published Courses of the actor's Publishers, for the grant form. */
  courses: z.array(z.object({ id: z.string(), title: z.string() })),
});

/**
 *
 */
export type OpsLearnerLookupResponse = z.infer<
  typeof opsLearnerLookupResponseSchema
>;

/** The body of the 403 a Learner whose Entitlement was revoked gets for the learn feed. */
export const ACCESS_ENDED_CODE = "access_ended";
